import axiosInstance from "@/lib/axiosInstance";
import type { CustomAxiosRequestConfig } from "@/types/common.types";

import { procurementEndpoints } from "./procurement.endpoints";

/*
 * SERV-2666: exported, because anything that changes a lease end date has to be able to invalidate
 * this. It used to be a local const inside NotificationBell, so no mutation could name it: creating
 * a unit whose lease was days away left the bell stale until its next poll, and TanStack does not
 * poll an unfocused tab at all - so in practice the alert did not appear until the page was
 * reloaded.
 */
export const ProcurementAlertQueryKeys = {
  list: "procurementExpiryAlerts",
} as const;

export type ProcurementAlertSeverity = "expired" | "urgent" | "soon";

/** SERV-2966: returns joined storage and rentals as a third thing the bell watches. */
export type ProcurementAlertKind = "storage" | "rental" | "return";

export type ProcurementExpiryAlert = {
  /** Stable key. A unit alert and a request alert can never collide on it. */
  id: string;
  kind: ProcurementAlertKind;
  severity: ProcurementAlertSeverity;
  /** Negative once the end date has passed. */
  daysRemaining: number;
  endsOn: string;
  message: string;
  /** What the reader is looking at: "PR-00012", or "Extra Space Storage B-114". */
  label: string;
  /*
   * SERV-2666: where the alert points. A storage unit rented through the units screen has no
   * request behind it, so these cannot be required. Exactly one of requestId / storageUnitId is
   * set, and it decides which screen the bell opens.
   */
  requestId: string | null;
  requestNumber: number | null;
  workOrderId: string | null;
  storageUnitId: string | null;
};

const asText = (value: unknown): string => (typeof value === "string" ? value.trim() : "");

const asNumber = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

/* Null and absent both mean "this alert does not point there", and must not become "" or 0. */
const asOptionalText = (value: unknown): string | null =>
  typeof value === "string" && value.trim() !== "" ? value.trim() : null;

const asOptionalNumber = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

/* Only for a response that predates `label`; the server formats it otherwise. */
const formatLegacyLabel = (requestNumber: unknown): string =>
  typeof requestNumber === "number" && Number.isFinite(requestNumber)
    ? `PR-${String(requestNumber).padStart(5, "0")}`
    : "";

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null ? (value as Record<string, unknown>) : {};

const SEVERITIES: ProcurementAlertSeverity[] = ["expired", "urgent", "soon"];

const asSeverity = (value: unknown): ProcurementAlertSeverity =>
  SEVERITIES.find((candidate) => candidate === value) ?? "soon";

/*
 * SERV-2966: this read `row.kind === "storage" ? "storage" : "rental"`, which quietly relabelled
 * anything it did not recognise as a rental - so a return-window alert would have reached the bell
 * calling itself a rental, and the officer would have been told to call off rent on a part going
 * back to a vendor.
 *
 * An unrecognised kind still falls back rather than dropping the alert, for the deploy-skew reason
 * given below: a frontend running ahead of its backend must not lose alerts it does not yet have a
 * word for.
 */
const asAlertKind = (value: unknown): ProcurementAlertKind =>
  value === "storage" ? "storage" : value === "return" ? "return" : "rental";

/**
 * These are computed server-side from the request dates and now, so there is no stored row to be
 * stale. A missing message is dropped rather than rendered blank: an alert with nothing to say is
 * worse than one fewer alert.
 */
export const getProcurementExpiryAlerts = async (
  signal?: AbortSignal,
): Promise<ProcurementExpiryAlert[]> => {
  const response = await axiosInstance.get(procurementEndpoints.expiryAlerts, {
    signal,
    suppressErrorAlert: true,
  } as CustomAxiosRequestConfig);
  const rows = asRecord(asRecord(response.data).data).alerts;
  if (!Array.isArray(rows)) return [];
  return (
    rows
      .map((entry) => {
        const row = asRecord(entry);
        return {
          /*
           * Falls back to the request id when the server does not send one. `id` arrived with
           * SERV-2666, and filtering on it means a frontend deployed ahead of its backend would
           * otherwise drop EVERY expiry alert, rentals included - a wider regression than the
           * storage-unit gap this change closes. Deployed the other way round only the new unit
           * alerts are missing, which is just the old behaviour.
           */
          id:
            asText(row.id) ||
            (asOptionalText(row.requestId) ? `request:${asText(row.requestId)}` : ""),
          kind: asAlertKind(row.kind),
          severity: asSeverity(row.severity),
          daysRemaining: asNumber(row.daysRemaining),
          endsOn: asText(row.endsOn),
          message: asText(row.message),
          label: asText(row.label) || formatLegacyLabel(row.requestNumber),
          requestId: asOptionalText(row.requestId),
          requestNumber: asOptionalNumber(row.requestNumber),
          workOrderId: asOptionalText(row.workOrderId),
          storageUnitId: asOptionalText(row.storageUnitId),
        };
      })
      /*
       * SERV-2666: filtered on the id, not on requestId. This used to require a request id, so once
       * storage units started alerting in their own right - which have none - every one of them was
       * dropped here, after the server had correctly sent it.
       */
      .filter((alert) => alert.id !== "" && alert.message !== "")
  );
};
